import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useTranslation } from 'react-i18next';
import { de } from 'date-fns/locale';
import { format as formatDate } from 'date-fns';
import ApiError from '@klab-berlin/api-sdk/lib/utils/ApiError';

import { NotificationContext } from '../../state/NotificationContextProvider';

import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';
import SettingItem from '../SettingItem';
import useScreenSize from '../../utils/useScreenSize';
import SubscriptionDetails from './SubscriptionDetails';
import services from '../../services';
import { ampliConversionPath, routes, subscriptionDateFormat } from '../../constants';
import useRouter from '../../utils/useRouter';
import useAction from '../../utils/useAction';
import {
  fetchMe,
  getSubscription,
  getStripeSubscriptionDetails as fetchStripeSubscriptionDetailsAction,
  getUpgradeOptions,
} from '../../actions/user.actions';
import { PlanKey } from '@klab-berlin/api-sdk/lib/types/requests/Billing';
import { STRIPE_PLAN_DISPLAY } from '../../constants/stripePlanBenefits';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';
import ManageSubscriptionDialog from '../../components/ManageSubscriptionDialog';
import SubscriptionStatusAlert from './SubscriptionStatusAlert';
import StripeCancelDialog from './StripeCancelDialog';
import StripeWithdrawalDialog from './StripeWithdrawalDialog';
import StripeManageSubscriptionDialog from './StripeManageSubscriptionDialog';
import StripePauseDialog from './StripePauseDialog';
import { getStripeSubscriptionDetails, hasOpenPauseRequest } from '../../reducers/user.selectors';
import useSubscriptionInfoData, { SubscriptionStatus } from '../../utils/useSubscriptionInfoData';
import { useAmplitudeExperiment } from '../../utils/ampli';
import config from 'config';
import { SubscriptionCancellationViewed } from '../../ampli';

// SM-433: the raw Stripe `price.recurring.interval` value we mirror from the
// backend's `subscription.interval` field. Only `'month'` is currently
// supported for pause; the FE fails closed on any other value (yearly, weekly,
// daily, missing).
const MONTHLY_BILLING_INTERVAL = 'month';

// Maps the API response status of POST /billings/cancel-subscription to a
// user-facing description. The endpoint returns 409 when the subscription is
// already cancelled / not cancellable, 404 when none is found, and 400/502 for
// request or upstream (kimuno) failures.
const cancelErrorDescriptionKey = (statusCode?: number): string => {
  switch (statusCode) {
    case 409:
      return 'Your subscription has already been cancelled or cannot be cancelled right now.';
    case 404:
      return 'We could not find an active subscription to cancel.';
    default:
      return 'Please try again later or contact our support team.';
  }
};

const statusConfig: Record<SubscriptionStatus, { label: string; color: string }> = {
  [SubscriptionStatus.Active]: { label: 'Active', color: 'success' },
  [SubscriptionStatus.Cancelled]: { label: 'Cancelled', color: 'warning' },
  [SubscriptionStatus.PauseRequested]: { label: 'Pause requested', color: 'info' },
  [SubscriptionStatus.Paused]: { label: 'Paused', color: 'info' },
  [SubscriptionStatus.Blocked]: { label: 'Blocked', color: 'error' },
  [SubscriptionStatus.WithdrawalRequested]: { label: 'Withdrawal submitted', color: 'info' },
};

export const endDateTitle = (status: SubscriptionStatus, isInTrial: boolean) => {
  if (isInTrial) {
    return 'Trial period ends at';
  }
  switch (status) {
    case SubscriptionStatus.Cancelled:
      return 'End date';

    case SubscriptionStatus.Blocked:
      return 'Blocked since';

    default:
      return 'Next renewal Date';
  }
};

const SubscriptionInfo = () => {
  const { t } = useTranslation();
  const { isXsScreen, isSmScreen } = useScreenSize();
  const { router } = useRouter();
  const { showNotification } = useContext(NotificationContext);
  const [isManageSubscriptionDialogOpen, setIsManageSubscriptionDialogOpen] = useState(false);
  const [isCreatingPortalSession, setIsCreatingPortalSession] = useState(false);
  const [isCancelStripeDialogOpen, setIsCancelStripeDialogOpen] = useState(false);
  const [isCancellingStripeSubscription, setIsCancellingStripeSubscription] = useState(false);
  const [isWithdrawalDialogOpen, setIsWithdrawalDialogOpen] = useState(false);
  const [isPauseDialogOpen, setIsPauseDialogOpen] = useState(false);
  // Optimistic override set after a successful in-app cancel, so the buttons
  // reflect the cancelled state even if the follow-up re-hydrate fails. When
  // false, both cancel signals below are derived from the Redux-hydrated Stripe
  // details (see canShowStripeCancelButton / isStripeSubscriptionCanceled).
  const [didCancelStripeSubscription, setDidCancelStripeSubscription] = useState(false);
  // SM-191: current Stripe plan key, from getUpgradeOptions (only the plan name
  // is shown — SM-291 removed the price line, which the Stripe portal already shows).
  const [stripePlan, setStripePlan] = useState<{ planKey: PlanKey } | null>(null);
  // SM-191: loading gate for the plan-card info (status label + name)
  // while the Stripe status/plan fetches are in flight.
  const [isStripeInfoLoading, setIsStripeInfoLoading] = useState(true);
  const refreshSubscription = useAction(getSubscription);
  const refreshMe = useAction(fetchMe);
  const hydrateStripeSubscriptionDetails = useAction(fetchStripeSubscriptionDetailsAction);
  const fetchUpgradeOptions = useAction(getUpgradeOptions);
  // SM-254: the kimuno-owned revocation (Widerruf) block for Stripe subscriptions.
  // Hydrated into Redux on login (AppPage dispatches getStripeSubscriptionDetails
  // for Stripe users); the `submitRevocation` thunk re-fetches it on success so
  // the "being processed" state appears without a reload.
  const stripeSubscriptionDetails = useSelector(getStripeSubscriptionDetails);
  const stripeSubscription = stripeSubscriptionDetails?.subscription;
  const revocation = stripeSubscriptionDetails?.revocation;
  // SM-151. Show the in-app "Cancel Subscription" button only once we know a
  // non-cancelled sub exists. Defaults to hidden while Redux is unhydrated or a
  // fetch failed, so the user falls back to the Stripe portal ("Manage Subscription").
  const canShowStripeCancelButton =
    !didCancelStripeSubscription && !!stripeSubscription && !stripeSubscription.isCanceled;
  // Inverse default: the trial "Probeabo widerrufen" button must stay shown for
  // the whole trial, so hide only once the sub is positively known cancelled.
  const isStripeSubscriptionCanceled =
    didCancelStripeSubscription || !!stripeSubscription?.isCanceled;
  const {
    user,
    subscription,
    isBasicUserWithoutSubscription,
    isB2CUserWithSubscription,
    subscriptionStatus,
    isStripeSubscriptionTerminated,
    startDate,
    endDate,
    isInTrial,
    withdrawalSubmittedAt,
    pauseStartDate,
    pauseEndDate,
  } = useSubscriptionInfoData();

  // SM-411. Feature-flag hook for the pause tile visibility rule below.
  // Called at the top of the component so the hook order is stable across
  // renders regardless of the Stripe/legacy branch we take. Uses the
  // `isEnabled` pattern (matches `useTrialReportCardEligibility` and the rest
  // of the codebase): `isEnabled = variant !== 'off'`, so an Amplitude-disabled
  // local dev environment (variant defaults to `'control'`) treats the flag
  // as ON — parity with the Zuora pause tile, which has no flag at all. Prod
  // turns the tile off explicitly via SM-414 setting the variant to `'off'`.
  const { isEnabled: isPauseFlagOn } = useAmplitudeExperiment(
    user,
    config.amplitudeExperiments.pauseSubscription,
  );

  useEffect(() => {
    if (!user?.isStripeUser) {
      setIsStripeInfoLoading(false);
      return;
    }

    let active = true;
    setIsStripeInfoLoading(true);
    // Only the plan name/key still needs a fetch here (getUpgradeOptions) — the
    // subscription-details endpoint carries no product/price fields. The
    // subscription status itself is read from Redux, hydrated once on login by
    // AppPage (and re-hydrated after cancel/withdrawal), so we no longer re-fetch
    // subscription-details on every Mein-Konto mount.
    fetchUpgradeOptions()
      .then((result) => {
        if (active && result) {
          setStripePlan({ planKey: result.currentPlanKey });
        }
      })
      .catch((error) => {
        notifyBugsnagHandledError(error as Error);
      })
      .finally(() => {
        if (active) setIsStripeInfoLoading(false);
      });

    return () => {
      active = false;
    };
    // fetchUpgradeOptions is a fresh reference each render; adding it would
    // re-run the effect every render. Fetch once per isStripeUser change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isStripeUser]);

  const conversionPath = ampliConversionPath.MyAccount;

  const onClickUpgrade = () => {
    if (user && conversionPath) {
      services.track.ampliTrackPricingPlansViewedEvent(
        conversionPath,
        subscription?.status
      );
    }
    // Mirrors SideNavbar's handleUpdatePlanClick: a Stripe user with a terminated
    // subscription falls through to this basic-user view (SM-383 above), but must
    // still go to the Stripe upgrade flow, not the legacy Zuora `/premium` page.
    if (user?.isStripeUser) {
      router.push(`/${routes.stripeUpgrade.root}`);
      return;
    }
    router.push(`/${routes.upgradePackageSelection.root}`);
  };

  // Stripe upgrade entry (the "Mein Abo anpassen" card in the Stripe manage
  // dialog). Distinct from the Zuora onClickUpgrade route above.
  const handleStripeUpgrade = () => {
    router.push(`/${routes.stripeUpgrade.root}`);
  };

  const handleManageSubscriptionDialogOpen = () => {
    setIsManageSubscriptionDialogOpen(true);

    services.track.ampliEventTrack({
      event: new SubscriptionCancellationViewed({ trial: isInTrial ? 'yes' : 'no' })
    });
  };

  const handleManageStripeBilling = async () => {
    if (isCreatingPortalSession) return;
    setIsCreatingPortalSession(true);

    // Open the tab synchronously so the browser ties it to the click and
    // doesn't block it as a popup; redirect it once the session URL resolves.
    const portalTab = window.open('', '_blank');
    if (portalTab) {
      portalTab.opener = null;
    }

    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const { url } = await services.billing.createPortalSession(returnUrl);

      if (portalTab) {
        portalTab.location.replace(url);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      portalTab?.close();
      showNotification({
        message: t('Could not open the billing portal. Please try again.'),
        severity: 'error',
      });
    } finally {
      setIsCreatingPortalSession(false);
    }
  };

  const handleCancelStripeSubscription = async (reason: string) => {
    if (isCancellingStripeSubscription) return;
    setIsCancellingStripeSubscription(true);

    try {
      // Persist the German label the user saw (mirrors the Zuora cancel flow),
      // not the English i18n key.
      const { effectiveEnd } = await services.billing.cancelStripeSubscription(t(reason));
      const formattedEndDate = formatDate(new Date(effectiveEnd), subscriptionDateFormat, { locale: de });

      // Reflect the cancelled state without a reload. The cancellation already
      // succeeded, so a refresh failure must not surface as a cancel failure — just
      // log it. SM-291: re-hydrate the Stripe details too (the chip + alert derive
      // from them via mapStripeSubscriptionStatus); refreshing only
      // getSubscription/fetchMe left the label stuck on "Aktiv" after a trial cancel.
      try {
        await Promise.all([
          hydrateStripeSubscriptionDetails(),
          refreshSubscription(),
          refreshMe(true),
        ]);
      } catch (refreshError) {
        notifyBugsnagHandledError(refreshError as Error);
      }

      setIsCancelStripeDialogOpen(false);
      // The subscription is now cancelled — hide the in-app cancel and trial
      // withdrawal buttons even if the re-hydrate above failed to land.
      setDidCancelStripeSubscription(true);
      showNotification({
        message: t('Your cancellation was successful'),
        description: t(
          'We have received your cancellation. Your access will remain active until {{endDate}}. ' +
          '– until then, you can continue to use meinUnterricht to its full potential',
          { endDate: formattedEndDate }
        ),
        severity: 'success',
      });
    } catch (error) {
      const statusCode = (error as ApiError)?.statusCode;
      setIsCancelStripeDialogOpen(false);
      showNotification({
        message: t('Your cancellation was not successful'),
        description: t(cancelErrorDescriptionKey(statusCode)),
        severity: 'error',
      });
      notifyBugsnagHandledError(error as Error);
    } finally {
      setIsCancellingStripeSubscription(false);
    }
  };

  // Stripe subscription start date (from the current period start), formatted for
  // the withdrawal dialog's read-only field. The legacy Zuora `subscription` start
  // date is empty for Stripe-only users, so derive it from the Stripe block.
  const stripePeriodStart = stripeSubscription?.periodStart;
  const stripeStartDate = stripePeriodStart
    ? formatDate(new Date(stripePeriodStart), subscriptionDateFormat, { locale: de })
    : '';

  // SM-383: a terminated Stripe subscription (status === 'canceled') has already
  // downgraded the user to Basis, so fall through to the basic-user view below
  // instead of the Stripe plan card — the latter would show a stale "verfügbar
  // bis {periodEnd}" banner promising access the user no longer has.
  if (user?.isStripeUser && !isStripeSubscriptionTerminated) {
    // New two-button layout in "mein Abo": a Stripe billing-portal button
    // plus one context action. Trial users get a direct "Probeabo widerrufen"
    // (cancel-during-trial); paid users get an "Abo verwalten" chooser that folds
    // the upgrade / withdrawal / cancel actions into a single dialog.
    const isStripeTrial = !!user?.isInTrial;
    const canUpgrade = !!user?.canUpgrade;
    const showWithdrawal = !!(revocation?.revocable && !revocation?.request);
    // Trial cancellation stays available for the whole trial. Default to shown
    // and hide only once the subscription is positively known cancelled,
    // so a slow/failed details fetch never strips the button.
    const showTrialCancel = isStripeTrial && !isStripeSubscriptionCanceled;
    // SM-411. Pause tile visibility. All of the following must hold:
    //   - Amplitude flag on (SM-414 flips it in prod) — resolved at the top
    //     of the component so the hook order is stable across renders.
    //   - subscription status is exactly Active — hides on trial / cancel /
    //     dunning / already-paused / pause-requested (map already handles all
    //     of these in `mapStripeSubscriptionStatus`).
    //   - no open pause request (dedup with the in-flight state — same
    //     `hasOpenPauseRequest` selector `mapStripeSubscriptionStatus` reads).
    //   - no in-flight withdrawal (revocation and pause are mutually exclusive).
    //   - not scheduled to cancel (a term-end cancel takes precedence).
    // Fail closed until we can positively verify eligibility against a hydrated
    // Stripe subscription. Same triple `canShowStripeCancelButton` uses:
    //   - `stripeSubscription` present  → the details fetch landed and returned
    //     a real subscription (guards `undefined` / `{ subscription: null }`)
    //   - not cancelled locally (`didCancelStripeSubscription`) or remotely
    //     (`stripeSubscription.isCanceled`) → catches the already-cancelled /
    //     just-cancelled states where a pause is nonsensical
    // Without these gates every downstream `?.` and `!hasOpenPauseRequest(...)`
    // reads as truthy against an absent details block and the tile shows for
    // states it shouldn't.
    // SM-433: pause is only supported on monthly-billed subs. Yearly is blocked
    // (Stripe's `pause_collection` alone doesn't extend the billing anchor, so
    // a yearly customer would lose N months of already-paid access with no
    // compensation); other intervals (weekly/daily/unknown) aren't supported
    // either. Fail-closed: show the tile ONLY when the interval is explicitly
    // `month`. The API belt-and-braces gate rejects with 409 if the button is
    // ever bypassed. Support handles non-monthly pause cases manually.
    //
    // The `interval` field is present in the backend response but not yet in
    // the installed api-sdk (v4.185.0) types — read via a narrow cast until
    // the SDK bump lands. Absent field → treated as unsupported (fail-closed).
    const stripeSubscriptionInterval = (
      stripeSubscription as { interval?: string | null } | null | undefined
    )?.interval;
    const showPause =
      !!stripeSubscription &&
      !isStripeSubscriptionCanceled &&
      isPauseFlagOn &&
      subscriptionStatus === SubscriptionStatus.Active &&
      !hasOpenPauseRequest(stripeSubscriptionDetails) &&
      !revocation?.request &&
      !stripeSubscription?.cancelAt &&
      stripeSubscriptionInterval === MONTHLY_BILLING_INTERVAL;
    // Hide "Abo verwalten" once the subscription is cancelled (SM-321): a cancelled
    // sub may still report canUpgrade/showWithdrawal, which otherwise kept the button
    // around. Use the canonical status from useSubscriptionInfoData so scheduled
    // term-end cancellations (`cancelAt`) and `status === 'canceled'` are caught too,
    // not just the local `isCanceled` signal. Also hide it when the dialog would have
    // no options to offer; either way the user can still self-manage via the Stripe
    // portal ("Rechnungen & Zahlungen").
    const showManageButton =
      !isStripeTrial &&
      subscriptionStatus !== SubscriptionStatus.Cancelled &&
      (canUpgrade || showPause || showWithdrawal || canShowStripeCancelButton);
    // SM-191: whether to show the subscription status label chip.
    const hasStripeSubscription = !!stripeSubscription;

    return (
      <Box>
        <Typography
          variant='h6'
          color={palette.text.primary}
          sx={{
            paddingY: spaces.md,
          }}
        >
          {t('Subscription Information')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: (isXsScreen || isSmScreen) ? '1fr' : '1fr 1fr',
            gap: spaces.md,
          }}
        >
          <Grid
            container
            direction='column'
            sx={{
              borderRadius: borderRadius.lg,
              border: `1px solid ${palette.elevation.outlined}`,
              padding: spaces.md,
              backgroundColor: palette.background.paperElevation2,
              gap: spaces.xs,
            }}
          >
            <Grid item>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  color='text.primary'
                  sx={{
                    fontFamily: 'Borna',
                    fontSize: '18px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '28px',
                    letterSpacing: '0.15px',
                  }}
                >
                  {t('Plan')}
                </Typography>
                {!isStripeInfoLoading && hasStripeSubscription && (
                  <Chip
                    label={t(statusConfig[subscriptionStatus]?.label)}
                    color={statusConfig[subscriptionStatus]?.color as 'success' | 'error' | 'warning' | 'info'}
                  />
                )}
              </Box>
            </Grid>
            {isStripeInfoLoading ? (
              <Grid item sx={{ display: 'flex', justifyContent: 'center', paddingY: spaces.lg }}>
                {/* Force the inner svg to fill the root box; MUI leaves it
                    unsized, so it renders smaller than the span and looks
                    off-center. Applies at any `size`. */}
                <CircularProgress sx={{ '& .MuiCircularProgress-svg': { width: '100%', height: '100%' } }} />
              </Grid>
            ) : (
              stripePlan && (
                <Grid item>
                  <Typography
                    variant='h6'
                    color='text.primary'
                  >
                    {t(STRIPE_PLAN_DISPLAY[stripePlan.planKey].packageName)}
                  </Typography>
                </Grid>
              )
            )}
            <Grid item>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spaces.md, marginTop: spaces.md }}>
                <Button
                  variant='contained'
                  color='primary'
                  size='large'
                  onClick={handleManageStripeBilling}
                  disabled={isCreatingPortalSession}
                  data-testid='stripe-billing-portal-button'
                >
                  {t('Invoices & Payments')}
                </Button>
                {showTrialCancel && (
                  <Button
                    variant='outlined'
                    color='inherit'
                    size='large'
                    onClick={() => setIsCancelStripeDialogOpen(true)}
                    data-testid='stripe-trial-withdraw-button'
                  >
                    {t('Cancel Trial Subscription')}
                  </Button>
                )}
                {showManageButton && (
                  <Button
                    variant='outlined'
                    color='inherit'
                    size='large'
                    onClick={() => setIsManageSubscriptionDialogOpen(true)}
                    data-testid='stripe-manage-subscription-button'
                  >
                    {t('Manage Subscription')}
                  </Button>
                )}
              </Box>
              <SubscriptionStatusAlert
                subscriptionStatus={subscriptionStatus}
                endDate={endDate}
                pauseStartDate={pauseStartDate}
                pauseEndDate={pauseEndDate}
                isBusinessUser={false}
                withdrawalSubmittedAt={withdrawalSubmittedAt}
              />
            </Grid>
          </Grid>
          {/* SM-291: gated on a hydrated subscription so the dates don't flash
              placeholder values while the details fetch is in flight. */}
          {!isStripeInfoLoading && hasStripeSubscription && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: spaces.md,
              }}
            >
              <SettingItem title={isStripeTrial ? t('Trial period started on') : t('Start date')}>
                <Typography>{startDate}</Typography>
              </SettingItem>
              <SettingItem title={t(endDateTitle(subscriptionStatus, isStripeTrial))}>
                <Typography>{endDate}</Typography>
              </SettingItem>
            </Box>
          )}
        </Box>
        <StripeManageSubscriptionDialog
          open={isManageSubscriptionDialogOpen}
          onClose={() => setIsManageSubscriptionDialogOpen(false)}
          canUpgrade={canUpgrade}
          onUpgrade={handleStripeUpgrade}
          showPause={showPause}
          onPause={() => {
            // SM-411. Fire the "Pause tile selected" Amplitude event at the
            // parent (mirrors how the sibling tiles delegate side-effects to
            // this file), then open the Stripe pause dialog.
            services.track.ampliTrackSubscriptionPauseSelectedEvent('');
            setIsPauseDialogOpen(true);
          }}
          showWithdrawal={showWithdrawal}
          onWithdraw={() => setIsWithdrawalDialogOpen(true)}
          showCancel={canShowStripeCancelButton}
          onCancel={() => setIsCancelStripeDialogOpen(true)}
        />
        <StripeWithdrawalDialog
          open={isWithdrawalDialogOpen}
          onClose={() => setIsWithdrawalDialogOpen(false)}
          email={user.email ?? ''}
          startDate={stripeStartDate}
        />
        <StripePauseDialog
          open={isPauseDialogOpen}
          onClose={() => setIsPauseDialogOpen(false)}
          user={user}
          subscription={subscription}
        />
        <StripeCancelDialog
          open={isCancelStripeDialogOpen}
          isTrial={isStripeTrial}
          isLoading={isCancellingStripeSubscription}
          onConfirm={handleCancelStripeSubscription}
          onClose={() => setIsCancelStripeDialogOpen(false)}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant='h6'
        color={palette.text.primary}
        sx={{
          paddingY: spaces.md,
        }}
      >
        {t('Subscription Information')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: (isXsScreen || isSmScreen) ? '1fr' : '1fr 1fr',
          gap: spaces.md,
        }}
      >
        <Grid
          container
          direction='column'
          sx={{
            borderRadius: borderRadius.lg,
            border: `1px solid ${palette.elevation.outlined}`,
            padding: spaces.md,
            backgroundColor: palette.background.paperElevation2,
            gap: spaces.xs,
          }}
        >
          <Grid item>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                color='text.primary'
                sx={{
                  fontFamily: 'Borna',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '28px',
                  letterSpacing: '0.15px',
                }}
              >
                {t('Plan')}
              </Typography>
              {!isBasicUserWithoutSubscription &&
                <Chip
                  label={t(statusConfig[subscriptionStatus]?.label)}
                  color={statusConfig[subscriptionStatus]?.color as 'success' | 'error' | 'warning' | 'info'}
                />}
            </Box>
          </Grid>
          <SubscriptionDetails
            isBasicUserWithoutSubscription={isBasicUserWithoutSubscription}
            onShowManageSubscriptionDialog={handleManageSubscriptionDialogOpen}
            endDate={endDate}
            subscriptionStatus={subscriptionStatus}
            onClickUpgrade={onClickUpgrade}
            onManageStripeBilling={handleManageStripeBilling}
            isCreatingStripePortalSession={isCreatingPortalSession}
          />
        </Grid>
        {isB2CUserWithSubscription && <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: spaces.md,
          }}
        >
          <SettingItem title={isInTrial ? t('Trial period started on') : t('Start date')}>
            <Typography>{startDate}</Typography>
          </SettingItem>
          <SettingItem title={t(endDateTitle(subscriptionStatus, isInTrial))}>
            <Typography>{endDate}</Typography>
          </SettingItem>
        </Box>}
      </Box>
      {user && subscription && (
        <ManageSubscriptionDialog
          open={isManageSubscriptionDialogOpen}
          onClose={() => setIsManageSubscriptionDialogOpen(false)}
          user={user}
          subscription={subscription}
          onClickUpgrade={onClickUpgrade}
        />
      )}

    </Box>
  );
};

export default SubscriptionInfo;
